import {useCallback} from 'react';
import {useKiosko} from '../useKiosko';
import {useKioskoStore} from '../useKioskoStore';
import type {ScanDocumentParams} from '../types';

export const useKioskoScannerControls = () => {
  const {sendMessage, handleResetScannerState} = useKiosko();
  const {
    connection: {isConnected, error},
    scanner: scannerState,
    setScannerState,
    apiKey,
  } = useKioskoStore();

  const initializeScanner = useCallback(() => {
    if (!isConnected) {
      console.warn('⚠️ Cannot initialize scanner: WebSocket not connected');
      return false;
    }

    setScannerState({isInitialized: false});
    return sendMessage('scanner_initialize', {}, apiKey);
  }, [isConnected, sendMessage, apiKey, setScannerState]);

  const scanDocument = useCallback(
    (params: ScanDocumentParams) => {
      if (!isConnected) {
        console.warn('⚠️ Cannot scan document: WebSocket not connected');
        return false;
      }

      if (!scannerState.isInitialized) {
        console.warn('⚠️ Scanner not initialized');
        return false;
      }

      const defaultParams = {
        source: 'Camera',
        mode: 'color',
        quality: 75,
        ...params,
      };

      setScannerState({isScanning: true});

      return sendMessage('scan_document', defaultParams, apiKey);
    },
    [isConnected, sendMessage, apiKey, setScannerState, scannerState.isInitialized],
  );

  return {
    isConnected,
    error,
    isInitialized: scannerState.isInitialized,
    isScanning: scannerState.isScanning,
    lastScanResult: scannerState.lastScanResult,
    success: scannerState.success,
    errorStatus: scannerState.error,
    initializeScanner,
    scanDocument,
    resetScannerState: handleResetScannerState,
  };
};
